/**
 * SRTHistoryPanel.jsx  — patched: individual + consolidated SRT download
 */

import { useState, useEffect, useCallback } from "react";
import SRTAnalytics from "./SRTAnalytics";

const C_SHARED = {
  navy: "#0B1F35", blue: "#1A4F7A", sky: "#2C7BB6",
  ink: "#182430", muted: "#5C7080", border: "#DDE4EC", fog: "#F2F5F8",
};

const ITEM_TITLES = {
  an01:"Pencil Grip",an02:"Shape Trace",an03:"Big & Small",an04:"Body Parts",
  an05:"Jump & Land",an06:"Scissors Use",an07:"Bead Stringing",an08:"Ball Catch",
  an09:"Balance Walk",an10:"Draw a Person",an11:"Colour Inside",an12:"Spatial Positions",
  an13:"Self-Care: Buttons",an14:"Right & Left",an15:"Throw & Aim",an16:"Copy Shapes",
  an17:"Clap Rhythm",an18:"Handwashing Steps",
  pr01:"Happy / Sad",pr02:"Turn Taking",pr03:"Fear & Surprise",pr04:"Greet Others",
  pr05:"Sharing",pr06:"Calm-Down Strategy",pr07:"Name Emotions",pr08:"Empathy Response",
  pr09:"Waiting in Line",pr10:"Express a Need",pr11:"Follow 2-Step Rule",pr12:"Approach New Task",
  pr13:"Group Participation",pr14:"Repair & Forgive",pr15:"Help Seeking",pr16:"Pretend Play",
  pr17:"Persistence",pr18:"My Favourite Thing",
  mn01:"Odd One Out",mn02:"AB Pattern",mn03:"Sort Animals/Vehicles",mn04:"3-Item Memory",
  mn05:"Cause & Effect",mn06:"ABC Pattern",mn07:"Find the Same",mn08:"Simple Problem Solving",
  mn09:"Sequence 3 Events",mn10:"Match by Function",mn11:"What If\u2026?",mn12:"Puzzle Focus",
  mn13:"True or False",mn14:"Two-Attribute Match",mn15:"3-Step Instruction",mn16:"What's Missing?",
  mn17:"Big \u2192 Small Order",mn18:"Story Retell",
  vj01:"Picture Description",vj02:"Starting Letter",vj03:"Count Objects",vj04:"More or Less",
  vj05:"Name 5 Letters",vj06:"Rhyming Words",vj07:"Number Order 1\u20135",vj08:"Find All Circles",
  vj09:"Own Name Reading",vj10:"One More",vj11:"First Sound",vj12:"Story Comprehension",
  vj13:"Blend Sounds",vj14:"Bigger Number",vj15:"Yesterday/Today/Tomorrow",vj16:"Make a Sentence",
  vj17:"Ordinal Numbers",vj18:"Write Own Name",
  aa01:"Favourite Colour",aa02:"Free Drawing",aa03:"Favourite Story",aa04:"Move to Music",
  aa05:"Favourite Animal",aa06:"Play Dough Creation",aa07:"Sing a Song",aa08:"Festival Knowledge",
  aa09:"Colour Mixing",aa10:"Nature Appreciation",aa11:"Body Shape",aa12:"Design a Flag",
  aa13:"Story Ending",aa14:"Puppet Play",aa15:"Texture Talk",aa16:"Joyful Memory",
  aa17:"Traditional Art",aa18:"Dream Invention",
};

const KOSHA_ORDER = [
  { id:"annamaya",    label:"Annamaya Kosha",    subtitle:"Physical & Motor",              prefix:"an", color:"#0E8A7A" },
  { id:"pranamaya",   label:"Pranamaya Kosha",   subtitle:"Socio-Emotional",               prefix:"pr", color:"#C2620A" },
  { id:"manomaya",    label:"Manomaya Kosha",    subtitle:"Cognitive",                     prefix:"mn", color:"#6E3EA6" },
  { id:"vijnanamaya", label:"Vijnanamaya Kosha", subtitle:"Language, Literacy & Numeracy", prefix:"vj", color:"#1A4F7A" },
  { id:"anandamaya",  label:"Anandamaya Kosha",  subtitle:"Aesthetic & Creative",          prefix:"aa", color:"#B03060" },
];

const LEVEL_COLOR = { "not-attempted":"#8A99A8", "emerging":"#C0392B", "developing":"#B06000", "achieved":"#1A7A40" };
const LEVEL_BG    = { "not-attempted":"#F0F4F8", "emerging":"#FDECEA",  "developing":"#FEF6E6",  "achieved":"#E8F5EE"  };

/* ── Build HTML for a single SRT session ── */
function buildSRTReportHTML(sess, childInfo = {}) {
  const date  = new Date(sess.date || sess.createdAt).toLocaleDateString("en-IN",{ day:"2-digit",month:"long",year:"numeric" });
  const childName    = childInfo.name || "—";
  const disabilities = (childInfo.disabilities||[]).join(", ")||"None declared";
  const age          = childInfo.dob ? Math.floor((new Date()-new Date(childInfo.dob))/31557600000)+"y" : "—";
  const ovr  = sess.overallScore ?? 0;
  const ovrC = ovr>=65?"#1A7A40":ovr>=40?"#B06000":ovr>0?"#C0392B":"#8A99A8";

  const koshaRows = KOSHA_ORDER.map(k=>{
    const pct=sess.koshaScores?.[k.id]??0;
    const bc=pct>=65?"#1A7A40":pct>=40?"#B06000":pct>0?"#C0392B":"#ddd";
    return `<tr>
      <td style="padding:7px 12px;font-size:12px;font-weight:600;color:#1A2E5A;width:220px">${k.label}<br/><span style="font-size:10px;color:#888;font-weight:400">${k.subtitle}</span></td>
      <td style="padding:7px 12px"><div style="background:#eee;border-radius:5px;height:10px"><div style="width:${pct}%;height:10px;background:${bc};border-radius:5px"></div></div></td>
      <td style="padding:7px 12px;font-size:14px;font-weight:800;color:${bc};text-align:right;width:60px">${pct}%</td>
    </tr>`;
  }).join("");

  const responses = sess.responses||{};
  let itemRows="",sc=0;
  for(const k of KOSHA_ORDER){
    let kR="",has=false;
    for(let n=1;n<=18;n++){
      const key=`${k.prefix}${String(n).padStart(2,"0")}`;
      const r=responses[key];
      if(!r||!r.interacted)continue;
      has=true;
      const lvl=r.level||"not-attempted";
      const lc=LEVEL_COLOR[lvl]||"#888",lb=LEVEL_BG[lvl]||"#f5f5f5";
      const cap=lvl.charAt(0).toUpperCase()+lvl.slice(1).replace("-"," ");
      kR+=`<tr style="border-bottom:1px solid #f5f5f5">
        <td style="padding:5px 10px;font-size:10px;color:#888;width:60px">${key.toUpperCase()}</td>
        <td style="padding:5px 10px;font-size:11px">${ITEM_TITLES[key]||key}</td>
        <td style="padding:5px 10px;text-align:center;width:100px"><span style="background:${lb};color:${lc};font-weight:700;font-size:10px;padding:2px 9px;border-radius:10px;border:1px solid ${lc}44">${cap}</span></td>
        <td style="padding:5px 10px;font-size:10px;color:#666;font-style:italic">${r.note||""}</td>
      </tr>`;
    }
    if(has){sc++;itemRows+=`<tr><td colspan="4" style="padding:10px 10px 4px;background:${k.color}12;border-left:4px solid ${k.color}">
      <span style="font-weight:800;font-size:12px;color:${k.color}">${k.label}</span>
      <span style="font-size:10px;color:${k.color};margin-left:8px">${k.subtitle}</span>
      <span style="float:right;font-size:12px;font-weight:800;color:${k.color}">${sess.koshaScores?.[k.id]??0}%</span>
    </td></tr>${kR}`;}
  }

  const cby=sess.conductedBy?.name?`by ${sess.conductedBy.name}`:"Staff session";
  const notes=sess.sessionNotes?`<div style="background:#f8f9ff;border-left:4px solid #1A4F7A;padding:12px 16px;margin:16px 0;border-radius:0 8px 8px 0"><div style="font-size:9px;font-weight:800;color:#888;letter-spacing:.5px;margin-bottom:4px">SESSION NOTES</div><div style="font-size:12px;color:#1A2E5A;line-height:1.7">${sess.sessionNotes}</div></div>`:"";

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>SRT — ${childName} — ${date}</title>
  <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;color:#1A2E5A}.no-print{display:block}@media print{.no-print{display:none!important}@page{size:A4;margin:10mm}}</style>
  </head><body>
  <div class="no-print" style="background:#0B1F35;color:white;padding:12px 24px;display:flex;justify-content:space-between;align-items:center;position:sticky;top:0;z-index:100">
    <span style="font-weight:700;font-size:14px">📋 SRT Report — ${childName} — ${date}</span>
    <div style="display:flex;gap:10px">
      <button onclick="window.print()" style="background:#F5D000;color:#0B1F35;border:none;padding:8px 20px;border-radius:7px;font-weight:800;font-size:12px;cursor:pointer">⬇ Download / Print PDF</button>
      <button onclick="window.close()" style="background:rgba(255,255,255,.15);color:white;border:1px solid rgba(255,255,255,.3);padding:8px 14px;border-radius:7px;font-size:12px;cursor:pointer">✕ Close</button>
    </div>
  </div>
  <div style="padding:32px 40px;max-width:820px;margin:0 auto">
    <div style="border-bottom:3px solid #0B1F35;padding-bottom:16px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:flex-start">
      <div>
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
          <div style="background:#0B1F35;color:white;font-weight:900;font-size:12px;padding:2px 8px;border-radius:3px">NISH</div>
          <div style="font-size:10px;color:#888">National Institute of Speech & Hearing · NCAHT · FSLP · ICMR</div>
        </div>
        <div style="font-size:22px;font-weight:900">${childName}</div>
        <div style="font-size:12px;color:#666;margin-top:3px">${disabilities}</div>
        <div style="font-size:11px;color:#888;margin-top:2px">${cby} · Age: ${age}</div>
      </div>
      <div style="text-align:right">
        <div style="font-size:10px;color:#888">Assessment Date</div>
        <div style="font-size:14px;font-weight:700">${date}</div>
        <div style="margin-top:12px"><div style="font-size:10px;color:#888">Overall Readiness</div>
          <div style="font-size:36px;font-weight:900;color:${ovrC};line-height:1">${ovr}%</div>
          <div style="font-size:10px;font-weight:700;color:${ovrC}">${ovr>=65?"Achieved":ovr>=40?"Developing":ovr>0?"Emerging":"Not Started"}</div>
        </div>
        <div style="font-size:10px;color:#888;margin-top:8px">${sess.itemsAttempted??0} of 90 items rated</div>
      </div>
    </div>
    <div style="margin-bottom:20px">
      <div style="font-size:13px;font-weight:800;margin-bottom:10px;color:#0B1F35">Panchakosha Domain Scores</div>
      <table style="width:100%;border-collapse:collapse">${koshaRows}</table>
    </div>
    ${notes}
    <div style="background:#FFFBEA;border:1.5px solid #E8C84A;border-radius:8px;padding:10px 14px;margin-bottom:20px;font-size:10px;color:#7A6000">
      <strong>Note:</strong> This is a play-based developmental profile under the Panchakosha–NCF-FS 2022 framework. It does not constitute a clinical diagnosis.
    </div>
    <div>
      <div style="font-size:13px;font-weight:800;margin-bottom:10px;color:#0B1F35">Item-by-Item Detail <span style="font-size:10px;font-weight:400;color:#888">${sess.itemsAttempted??0} items · ${sc} domains</span></div>
      <table style="width:100%;border-collapse:collapse;font-size:11px">
        <tr style="background:#0B1F35;color:white">
          <th style="padding:7px 10px;text-align:left">Code</th><th style="padding:7px 10px;text-align:left">Item</th>
          <th style="padding:7px 10px;text-align:center;width:110px">Level</th><th style="padding:7px 10px;text-align:left">Assessor Note</th>
        </tr>
        ${itemRows}
      </table>
    </div>
    <div style="margin-top:28px;border-top:1px solid #ddd;padding-top:14px;display:flex;justify-content:space-between;font-size:9px;color:#aaa">
      <span>Assessed by: ___________________________  Designation: _______________</span>
      <span>Signature: ___________________________  Date: ${date}</span>
    </div>
    <div style="text-align:center;font-size:8px;color:#ccc;margin-top:6px">© NISH Thiruvananthapuram · NCAHT · ICMR · Vidyagni FSLP · iate.ncahtnish.in</div>
  </div></body></html>`;
}

/* ── Build consolidated HTML for ALL sessions ── */
function buildConsolidatedHTML(sessions, childInfo = {}) {
  const childName    = childInfo.name || "—";
  const disabilities = (childInfo.disabilities||[]).join(", ")||"None declared";
  const age          = childInfo.dob ? Math.floor((new Date()-new Date(childInfo.dob))/31557600000)+"y" : "—";
  const sorted = [...sessions].sort((a,b)=>new Date(a.date||a.createdAt)-new Date(b.date||b.createdAt));

  const trajRows = sorted.map((sess,i)=>{
    const d  = new Date(sess.date||sess.createdAt).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
    const ovr = sess.overallScore??0;
    const oc  = ovr>=65?"#1A7A40":ovr>=40?"#B06000":ovr>0?"#C0392B":"#aaa";
    const kCells = KOSHA_ORDER.map(k=>{
      const pct=sess.koshaScores?.[k.id]??0;
      const c=pct>=65?"#1A7A40":pct>=40?"#B06000":pct>0?"#C0392B":"#aaa";
      return `<td style="padding:6px 10px;text-align:center;font-weight:700;color:${c}">${pct>0?pct+"%":"—"}</td>`;
    }).join("");
    const delta = i>0?(ovr-(sorted[i-1].overallScore??0)):null;
    const ds = delta!==null?`<span style="color:${delta>0?"#1A7A40":delta<0?"#C0392B":"#888"};font-size:10px">${delta>0?"+":""}${delta}%</span>`:"";
    return `<tr style="border-bottom:1px solid #f0f0f0${i===sorted.length-1?";background:#f8faff":""}">
      <td style="padding:7px 10px;font-size:11px;color:#555">${i+1}</td>
      <td style="padding:7px 10px;font-size:11px;font-weight:600">${d}</td>
      ${kCells}
      <td style="padding:7px 10px;text-align:center"><span style="font-weight:900;font-size:14px;color:${oc}">${ovr>0?ovr+"%":"—"}</span>${ds?`<br/>${ds}`:""}</td>
      <td style="padding:7px 10px;font-size:10px;color:#888">${sess.itemsAttempted??0} items</td>
    </tr>`;
  }).join("");

  const sessionSections = sorted.map((sess,i)=>{
    const date = new Date(sess.date||sess.createdAt).toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"});
    const ovr = sess.overallScore??0;
    const oc  = ovr>=65?"#1A7A40":ovr>=40?"#B06000":ovr>0?"#C0392B":"#aaa";
    const responses = sess.responses||{};
    const koshaRows = KOSHA_ORDER.map(k=>{
      const pct=sess.koshaScores?.[k.id]??0;
      const bc=pct>=65?"#1A7A40":pct>=40?"#B06000":pct>0?"#C0392B":"#ddd";
      return `<tr><td style="padding:5px 10px;font-size:11px;font-weight:600;color:#1A2E5A;width:190px">${k.label}</td>
        <td style="padding:5px 10px"><div style="background:#eee;border-radius:4px;height:8px"><div style="width:${pct}%;height:8px;background:${bc};border-radius:4px"></div></div></td>
        <td style="padding:5px 10px;font-weight:800;color:${bc};text-align:right;width:50px">${pct}%</td></tr>`;
    }).join("");
    let itemRows="";
    for(const k of KOSHA_ORDER){
      let kR="",has=false;
      for(let n=1;n<=18;n++){
        const key=`${k.prefix}${String(n).padStart(2,"0")}`;
        const r=responses[key];
        if(!r||!r.interacted)continue;
        has=true;
        const lvl=r.level||"not-attempted";
        const lc=LEVEL_COLOR[lvl]||"#888",lb=LEVEL_BG[lvl]||"#f5f5f5";
        const cap=lvl.charAt(0).toUpperCase()+lvl.slice(1).replace("-"," ");
        kR+=`<tr style="border-bottom:1px solid #f8f8f8">
          <td style="padding:4px 8px;font-size:9px;color:#888;width:55px">${key.toUpperCase()}</td>
          <td style="padding:4px 8px;font-size:10px">${ITEM_TITLES[key]||key}</td>
          <td style="padding:4px 8px;text-align:center;width:90px"><span style="background:${lb};color:${lc};font-weight:700;font-size:9px;padding:1px 7px;border-radius:8px">${cap}</span></td>
          <td style="padding:4px 8px;font-size:9px;color:#777;font-style:italic">${r.note||""}</td>
        </tr>`;
      }
      if(has)itemRows+=`<tr><td colspan="4" style="padding:8px 8px 3px;background:${k.color}10;border-left:3px solid ${k.color}">
        <span style="font-weight:800;font-size:11px;color:${k.color}">${k.label}</span>
        <span style="float:right;font-weight:800;font-size:11px;color:${k.color}">${sess.koshaScores?.[k.id]??0}%</span>
      </td></tr>${kR}`;
    }
    const notes=sess.sessionNotes?`<div style="background:#f8f9ff;border-left:3px solid #1A4F7A;padding:10px 14px;margin:12px 0;border-radius:0 6px 6px 0;font-size:11px;color:#1A2E5A;line-height:1.6"><strong>Notes:</strong> ${sess.sessionNotes}</div>`:"";
    return `<div style="margin-bottom:36px;page-break-before:${i>0?"always":"auto"}">
      <div style="background:#0B1F35;color:white;padding:12px 16px;border-radius:10px 10px 0 0;display:flex;justify-content:space-between;align-items:center">
        <div><span style="font-weight:800;font-size:14px">Session ${i+1}</span><span style="font-size:12px;color:rgba(255,255,255,.6);margin-left:10px">${date}</span></div>
        <div><span style="font-size:22px;font-weight:900;color:${oc}">${ovr}%</span><span style="font-size:10px;color:rgba(255,255,255,.5);margin-left:6px">${sess.itemsAttempted??0} items</span></div>
      </div>
      <div style="border:1.5px solid #e0e0e0;border-top:none;border-radius:0 0 10px 10px;padding:16px">
        <table style="width:100%;border-collapse:collapse;margin-bottom:14px">${koshaRows}</table>
        ${notes}
        <div style="font-size:11px;font-weight:800;color:#0B1F35;margin-bottom:8px">Item Detail</div>
        <table style="width:100%;border-collapse:collapse;font-size:10px">
          <tr style="background:#1A2E5A;color:white"><th style="padding:5px 8px;text-align:left">Code</th><th style="padding:5px 8px;text-align:left">Item</th><th style="padding:5px 8px;text-align:center">Level</th><th style="padding:5px 8px;text-align:left">Note</th></tr>
          ${itemRows}
        </table>
      </div>
    </div>`;
  }).join("");

  const from=new Date(sorted[0].date||sorted[0].createdAt).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
  const to  =new Date(sorted[sorted.length-1].date||sorted[sorted.length-1].createdAt).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>SRT History — ${childName}</title>
  <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;color:#1A2E5A}.no-print{display:block}@media print{.no-print{display:none!important}@page{size:A4;margin:10mm}}</style>
  </head><body>
  <div class="no-print" style="background:#0B1F35;color:white;padding:12px 24px;display:flex;justify-content:space-between;align-items:center;position:sticky;top:0;z-index:100">
    <span style="font-weight:700;font-size:14px">📋 SRT History — ${childName} — ${sorted.length} sessions</span>
    <div style="display:flex;gap:10px">
      <button onclick="window.print()" style="background:#F5D000;color:#0B1F35;border:none;padding:8px 20px;border-radius:7px;font-weight:800;font-size:12px;cursor:pointer">⬇ Download / Print PDF</button>
      <button onclick="window.close()" style="background:rgba(255,255,255,.15);color:white;border:1px solid rgba(255,255,255,.3);padding:8px 14px;border-radius:7px;font-size:12px;cursor:pointer">✕ Close</button>
    </div>
  </div>
  <div style="padding:32px 40px;max-width:860px;margin:0 auto">
    <div style="border-bottom:3px solid #0B1F35;padding-bottom:16px;margin-bottom:22px;display:flex;justify-content:space-between;align-items:flex-end">
      <div>
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
          <div style="background:#0B1F35;color:white;font-weight:900;font-size:12px;padding:2px 8px;border-radius:3px">NISH</div>
          <div style="font-size:10px;color:#888">National Institute of Speech & Hearing · NCAHT · FSLP · ICMR</div>
        </div>
        <div style="font-size:22px;font-weight:900">${childName}</div>
        <div style="font-size:12px;color:#666;margin-top:2px">${disabilities}</div>
        <div style="font-size:11px;color:#888;margin-top:2px">Age: ${age}</div>
      </div>
      <div style="text-align:right">
        <div style="font-size:13px;font-weight:800;color:#0B1F35">SRT Progress History</div>
        <div style="font-size:11px;color:#888;margin-top:3px">${sorted.length} sessions · Panchakosha · NCF-FS 2022</div>
        <div style="font-size:10px;color:#aaa;margin-top:3px">${from} → ${to}</div>
      </div>
    </div>
    <div style="margin-bottom:28px">
      <div style="font-size:13px;font-weight:800;color:#0B1F35;margin-bottom:10px">Progress Trajectory</div>
      <table style="width:100%;border-collapse:collapse;font-size:11px">
        <tr style="background:#0B1F35;color:white">
          <th style="padding:7px 10px;text-align:left">#</th>
          <th style="padding:7px 10px;text-align:left">Date</th>
          ${KOSHA_ORDER.map(k=>`<th style="padding:7px 10px;text-align:center">${k.label.split(" ")[0]}</th>`).join("")}
          <th style="padding:7px 10px;text-align:center">Overall</th>
          <th style="padding:7px 10px;text-align:left">Items</th>
        </tr>
        ${trajRows}
      </table>
      <div style="font-size:9px;color:#aaa;margin-top:5px">Green ≥65% · Amber 40–64% · Red &lt;40% · Δ = change from previous session</div>
    </div>
    <div style="background:#FFFBEA;border:1.5px solid #E8C84A;border-radius:8px;padding:10px 14px;margin-bottom:24px;font-size:10px;color:#7A6000">
      <strong>Note:</strong> Play-based developmental profile · Panchakosha–NCF-FS 2022 · Not a clinical diagnosis.
    </div>
    <div style="font-size:14px;font-weight:800;color:#0B1F35;margin-bottom:16px;padding-bottom:8px;border-bottom:2px solid #0B1F35">Detailed Session Records</div>
    ${sessionSections}
    <div style="border-top:1px solid #ddd;margin-top:24px;padding-top:12px;text-align:center;font-size:8px;color:#ccc">
      © NISH Thiruvananthapuram · NCAHT · ICMR · Vidyagni FSLP · iate.ncahtnish.in
    </div>
  </div></body></html>`;
}

function openReport(html) {
  const win = window.open("","_blank");
  if(!win){ alert("Please allow pop-ups to open reports."); return; }
  win.document.write(html);
  win.document.close();
}

/* ── KoshaBars ── */
function KoshaBars({ scores, definitions, compact=false }) {
  if(!scores) return null;
  return (
    <div style={{ display:"flex",gap:compact?4:6,marginTop:6 }}>
      {definitions.map(k=>{
        const pct=scores[k.id]??0;
        return (
          <div key={k.id} title={`${k.label||k.short}: ${pct}%`}
            style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2 }}>
            <div style={{ width:"100%",height:compact?4:5,borderRadius:3,background:pct>0?k.color:"rgba(0,0,0,.08)" }}/>
            <span style={{ fontSize:compact?8:9,color:pct>0?k.color:C_SHARED.muted,fontWeight:700 }}>{pct>0?`${pct}%`:"–"}</span>
            {!compact&&<span style={{ fontSize:7,color:C_SHARED.muted }}>{k.short||k.label}</span>}
          </div>
        );
      })}
    </div>
  );
}

/* ── RecBadge ── */
function RecBadge({ rec, small=false }) {
  const cfg={
    "no-concerns":{ label:"No Concerns",color:"#1A7A40",bg:"#E8F5EE",icon:"✅" },
    "monitor":    { label:"Monitor",    color:"#B06000",bg:"#FEF3E2",icon:"⚠️" },
    "full-srt":   { label:"Full SRT",  color:"#C0392B",bg:"#FDECEA",icon:"🔴" },
  }[rec]||{ label:rec,color:C_SHARED.muted,bg:C_SHARED.fog,icon:"?" };
  return (
    <div style={{ display:"inline-flex",alignItems:"center",gap:4,background:cfg.bg,color:cfg.color,
      fontSize:small?9:10,fontWeight:800,padding:small?"1px 7px":"3px 10px",borderRadius:20,
      border:`1px solid ${cfg.color}33` }}>
      {cfg.icon} {cfg.label}
    </div>
  );
}

/* ── SRTCard — with ⬇ Report button on header ── */
function SRTCard({ sess, isCurrent, isLoading, onLoad, onPrint, onDownload, KOSHAS, levelFor, index, isFirst }) {
  const [open,setOpen] = useState(false);
  const d       = new Date(sess.date||sess.createdAt);
  const dateStr = d.toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"});
  const timeStr = d.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
  const ovr = sess.overallScore??0;
  const lv  = levelFor(ovr);

  return (
    <div style={{ background:"white",borderRadius:13,
      border:isCurrent?`2px solid ${C_SHARED.sky}`:`1.5px solid ${C_SHARED.border}`,
      boxShadow:isCurrent?`0 4px 18px ${C_SHARED.sky}22`:"0 1px 5px rgba(0,0,0,.05)",
      overflow:"hidden" }}>

      <div onClick={()=>setOpen(o=>!o)}
        style={{ padding:"13px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,
          background:open?"#F8FBFF":"white",borderBottom:open?`1px solid ${C_SHARED.border}`:"none" }}>

        <div style={{ flexShrink:0,width:36,height:36,borderRadius:9,background:"#EBF2FA",
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:16 }}>🎮</div>

        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ display:"flex",alignItems:"center",gap:7,flexWrap:"wrap" }}>
            <span style={{ fontWeight:800,fontSize:14,color:C_SHARED.ink }}>{dateStr}</span>
            <span style={{ fontSize:11,color:C_SHARED.muted }}>{timeStr}</span>
            {isCurrent&&<span style={{ background:"#E8F0FE",color:C_SHARED.blue,fontSize:9,fontWeight:700,padding:"1px 8px",borderRadius:10 }}>● current</span>}
            {isFirst&&!isCurrent&&<span style={{ background:"#E8F5EE",color:"#1A7A40",fontSize:9,fontWeight:700,padding:"1px 8px",borderRadius:10 }}>latest</span>}
            <span style={{ background:"#EBF2FA",color:C_SHARED.blue,fontSize:9,fontWeight:700,padding:"1px 7px",borderRadius:8 }}>Full SRT · 90 items</span>
          </div>
          <div style={{ fontSize:11,color:C_SHARED.muted,marginTop:2 }}>
            {sess.conductedBy?.name?`by ${sess.conductedBy.name}`:"Staff session"}
            {sess.itemsAttempted!=null?` · ${sess.itemsAttempted} items attempted`:""}
          </div>
        </div>

        <div style={{ textAlign:"center",flexShrink:0 }}>
          <div style={{ fontSize:26,fontWeight:900,color:ovr>0?lv.color:C_SHARED.muted,lineHeight:1,fontFamily:"Georgia,serif" }}>
            {ovr>0?`${ovr}%`:"–"}
          </div>
          {ovr>0&&<div style={{ background:lv.bg,color:lv.color,fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:8,marginTop:3,display:"inline-block" }}>{lv.label}</div>}
        </div>

        {/* Individual download — always visible on card header */}
        <button onClick={e=>{e.stopPropagation();onDownload(sess);}} title="Download this session report"
          style={{ flexShrink:0,background:"#EBF2FA",color:C_SHARED.blue,
            border:`1.5px solid ${C_SHARED.sky}44`,borderRadius:8,padding:"6px 12px",
            fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
            display:"flex",alignItems:"center",gap:4 }}>
          ⬇ Report
        </button>

        <div style={{ fontSize:11,color:C_SHARED.muted,flexShrink:0,
          transform:open?"rotate(180deg)":"none",transition:"transform .2s" }}>▾</div>
      </div>

      {sess.koshaScores&&(
        <div style={{ padding:"8px 16px",borderBottom:open?`1px solid ${C_SHARED.border}`:"none" }}>
          <KoshaBars scores={sess.koshaScores} definitions={KOSHAS}/>
        </div>
      )}

      {open&&(
        <div style={{ padding:"14px 16px" }}>
          {sess.koshaScores&&(
            <div style={{ display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8,marginBottom:14 }}>
              {KOSHAS.map(k=>{
                const pct=sess.koshaScores[k.id]??0;
                const klv=levelFor(pct);
                return (
                  <div key={k.id} style={{ textAlign:"center",background:k.bg,borderRadius:10,
                    padding:"10px 6px",border:`1px solid ${k.color}22` }}>
                    <div style={{ fontSize:18,marginBottom:4 }}>{k.icon}</div>
                    <div style={{ fontSize:16,fontWeight:900,color:klv.color,fontFamily:"Georgia,serif",lineHeight:1 }}>
                      {pct>0?`${pct}%`:"–"}
                    </div>
                    <div style={{ fontSize:9,fontWeight:700,color:k.color,marginTop:3 }}>{k.short}</div>
                    {pct>0&&<div style={{ fontSize:8,color:klv.color,marginTop:2,fontWeight:700 }}>{klv.label}</div>}
                  </div>
                );
              })}
            </div>
          )}
          {sess.sessionNotes&&(
            <div style={{ background:C_SHARED.fog,borderRadius:8,padding:"10px 12px",marginBottom:12,
              fontSize:12,color:C_SHARED.ink,lineHeight:1.7,borderLeft:`3px solid ${C_SHARED.border}` }}>
              <div style={{ fontSize:9,fontWeight:800,color:C_SHARED.muted,letterSpacing:.5,marginBottom:4 }}>SESSION NOTES</div>
              {sess.sessionNotes}
            </div>
          )}
          <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
            <button onClick={()=>onLoad(sess)} disabled={isLoading}
              style={{ padding:"7px 16px",background:isCurrent?C_SHARED.fog:C_SHARED.sky,
                color:isCurrent?C_SHARED.muted:"white",border:`1.5px solid ${isCurrent?C_SHARED.border:C_SHARED.sky}`,
                borderRadius:8,fontSize:11,fontWeight:800,cursor:isLoading?"not-allowed":"pointer",fontFamily:"inherit" }}>
              {isCurrent?"▶ Resume":"📂 Load & Continue"}
            </button>
            {onPrint&&(
              <button onClick={()=>onPrint(sess)}
                style={{ padding:"7px 14px",background:"white",color:C_SHARED.muted,
                  border:`1.5px solid ${C_SHARED.border}`,borderRadius:8,fontSize:11,fontWeight:700,
                  cursor:"pointer",fontFamily:"inherit" }}>
                🖨️ Print Report
              </button>
            )}
            <button onClick={()=>onDownload(sess)}
              style={{ padding:"7px 14px",background:"#EBF2FA",color:C_SHARED.blue,
                border:`1.5px solid ${C_SHARED.sky}44`,borderRadius:8,fontSize:11,fontWeight:700,
                cursor:"pointer",fontFamily:"inherit" }}>
              ⬇ Download Report
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── QSCard (unchanged from original) ── */
function QSCard({ result, isLoading, onLoad, onPrint, DOMAINS, isFirst }) {
  const [open,setOpen] = useState(false);
  const d       = new Date(result.date||result.createdAt);
  const dateStr = d.toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"});
  const timeStr = d.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
  const ovr = result.overallScore??0;
  const rec = result.recommendation||"monitor";
  const flagColor={"no-concerns":"#1A7A40","monitor":"#B06000","full-srt":"#C0392B"};
  const fc = flagColor[rec]||C_SHARED.muted;

  return (
    <div style={{ background:"white",borderRadius:13,border:`1.5px solid ${C_SHARED.border}`,
      boxShadow:"0 1px 5px rgba(0,0,0,.05)",overflow:"hidden" }}>
      <div onClick={()=>setOpen(o=>!o)}
        style={{ padding:"13px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:12,
          background:open?"#FFFDF7":"white",borderBottom:open?`1px solid ${C_SHARED.border}`:"none" }}>
        <div style={{ flexShrink:0,width:36,height:36,borderRadius:9,background:"#FEF3E2",
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:16 }}>⚡</div>
        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ display:"flex",alignItems:"center",gap:7,flexWrap:"wrap" }}>
            <span style={{ fontWeight:800,fontSize:14,color:C_SHARED.ink }}>{dateStr}</span>
            <span style={{ fontSize:11,color:C_SHARED.muted }}>{timeStr}</span>
            {isFirst&&<span style={{ background:"#E8F5EE",color:"#1A7A40",fontSize:9,fontWeight:700,padding:"1px 8px",borderRadius:10 }}>latest</span>}
            <span style={{ background:"#FEF0E6",color:"#C2620A",fontSize:9,fontWeight:700,padding:"1px 7px",borderRadius:8 }}>Quick Screener · 10 activities</span>
          </div>
          <div style={{ fontSize:11,color:C_SHARED.muted,marginTop:2 }}>
            {result.screenedBy?.name?`by ${result.screenedBy.name}`:"Staff screening"}
          </div>
        </div>
        <div style={{ textAlign:"right",flexShrink:0,display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4 }}>
          {ovr>0&&<div style={{ fontSize:22,fontWeight:900,color:fc,fontFamily:"Georgia,serif",lineHeight:1 }}>{ovr}%</div>}
          <RecBadge rec={rec}/>
        </div>
        <div style={{ fontSize:11,color:C_SHARED.muted,flexShrink:0,transform:open?"rotate(180deg)":"none",transition:"transform .2s" }}>▾</div>
      </div>
      {result.domainScores&&(
        <div style={{ padding:"8px 16px",borderBottom:open?`1px solid ${C_SHARED.border}`:"none" }}>
          <KoshaBars scores={result.domainScores} definitions={DOMAINS}/>
        </div>
      )}
      {open&&(
        <div style={{ padding:"14px 16px" }}>
          {result.domainScores&&(
            <div style={{ display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8,marginBottom:14 }}>
              {DOMAINS.map(d=>{
                const pct=result.domainScores[d.id]??0;
                const df=result.flags?.[d.id]||"amber";
                const fc2={"green":"#1A7A40","amber":"#B06000","red":"#C0392B"}[df]||C_SHARED.muted;
                const fb2={"green":"#E8F5EE","amber":"#FEF3E2","red":"#FDECEA"}[df]||C_SHARED.fog;
                return (
                  <div key={d.id} style={{ textAlign:"center",background:fb2,borderRadius:10,padding:"10px 6px",border:`1px solid ${fc2}22` }}>
                    <div style={{ fontSize:18,marginBottom:4 }}>{d.icon}</div>
                    <div style={{ fontSize:16,fontWeight:900,color:fc2,fontFamily:"Georgia,serif",lineHeight:1 }}>{pct>0?`${pct}%`:"–"}</div>
                    <div style={{ fontSize:9,fontWeight:700,color:d.color,marginTop:3 }}>{d.label}</div>
                    <div style={{ fontSize:8,color:fc2,marginTop:2,fontWeight:700 }}>{df==="green"?"✅ OK":df==="amber"?"⚠️ Watch":"🔴 Refer"}</div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ background:C_SHARED.fog,borderRadius:8,padding:"10px 12px",marginBottom:12,
            fontSize:12,color:C_SHARED.ink,lineHeight:1.7,borderLeft:`3px solid ${fc}` }}>
            <div style={{ fontSize:9,fontWeight:800,color:C_SHARED.muted,letterSpacing:.5,marginBottom:4 }}>SCREENING DECISION</div>
            {{"no-concerns":"No concerns found. Re-screen in 6 months.",
              "monitor":"Emerging skills in 1+ domains. Provide targeted support and re-screen in 8–10 weeks.",
              "full-srt":"Significantly delayed skills in 2+ domains. Full 90-item SRT recommended."}[rec]||"–"}
          </div>
          <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
            {onLoad&&(
              <button onClick={()=>onLoad(result)} disabled={isLoading}
                style={{ padding:"7px 16px",background:"#FEF0E6",color:"#C2620A",border:"1.5px solid #C2620A44",
                  borderRadius:8,fontSize:11,fontWeight:800,cursor:isLoading?"not-allowed":"pointer",fontFamily:"inherit" }}>
                📂 View in Screener
              </button>
            )}
            {onPrint&&(
              <button onClick={()=>onPrint(result)}
                style={{ padding:"7px 14px",background:"white",color:C_SHARED.muted,
                  border:`1.5px solid ${C_SHARED.border}`,borderRadius:8,fontSize:11,fontWeight:700,
                  cursor:"pointer",fontFamily:"inherit" }}>
                🖨️ Print Screening
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN EXPORT
══════════════════════════════════════════════════════════════════════════ */
export default function SRTHistoryPanel({
  childId,
  currentSessId = null,
  mode = "srt-only",
  onLoadSRT, onLoadQS,
  getAuthHeader,
  KOSHAS, DOMAINS,
  levelFor,
  printSRT, printScreening,
  API,
  child = null,   // pass child obj { name, dob, disabilities } for richer reports
}) {
  const [srtSessions, setSrtSessions] = useState([]);
  const [qsResults,   setQsResults]   = useState([]);
  const [loadingSRT,  setLoadingSRT]  = useState(false);
  const [loadingQS,   setLoadingQS]   = useState(false);
  const [loadingItem, setLoadingItem] = useState(false);

  const fetchSRT = useCallback(async()=>{
    if(!childId) return;
    setLoadingSRT(true);
    try{
      const res=await fetch(`${API}/srt/${childId}?limit=30`,{headers:getAuthHeader()});
      const data=await res.json();
      if(data.success) setSrtSessions(data.sessions||[]);
    }catch{}
    setLoadingSRT(false);
  },[childId,API,getAuthHeader]);

  const fetchQS = useCallback(async()=>{
    if(!childId) return;
    setLoadingQS(true);
    try{
      const res=await fetch(`${API}/srt-screener/${childId}?limit=30`,{headers:getAuthHeader()});
      const data=await res.json();
      if(data.success) setQsResults(data.results||[]);
    }catch{}
    setLoadingQS(false);
  },[childId,API,getAuthHeader]);

  useEffect(()=>{ fetchSRT(); fetchQS(); },[fetchSRT,fetchQS]);

  const handleLoadSRT = async(sess)=>{ if(!onLoadSRT) return; setLoadingItem(true); await onLoadSRT(sess); setLoadingItem(false); };
  const handleLoadQS  = async(r)=>  { if(!onLoadQS)  return; setLoadingItem(true); await onLoadQS(r);    setLoadingItem(false); };

  const handleDownloadSingle = (sess) => openReport(buildSRTReportHTML(sess, child||{}));
  const handleDownloadAll    = ()     => { if(srtSessions.length) openReport(buildConsolidatedHTML(srtSessions, child||{})); };

  const isLoading = loadingSRT||loadingQS;

  /* ── COMBINED MODE ── */
  if(mode==="combined"){
    const merged=[
      ...srtSessions.map(s=>({...s,_type:"srt",_sortDate:new Date(s.date||s.createdAt)})),
      ...qsResults.map(r=> ({...r,_type:"qs", _sortDate:new Date(r.date||r.createdAt)})),
    ].sort((a,b)=>b._sortDate-a._sortDate);

    return (
      <div>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16 }}>
          <div>
            <div style={{ fontWeight:900,fontSize:16 }}>Assessment History</div>
            <div style={{ fontSize:12,color:C_SHARED.muted,marginTop:2 }}>
              {isLoading?"Loading…":`${srtSessions.length} full SRT · ${qsResults.length} quick screening`}
            </div>
          </div>
          <div style={{ display:"flex",gap:8 }}>
            {srtSessions.length>0&&(
              <button onClick={handleDownloadAll}
                style={{ background:"#0B1F35",color:"white",border:"none",borderRadius:8,
                  padding:"7px 16px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                  display:"flex",alignItems:"center",gap:5 }}>
                ⬇ All SRT Reports ({srtSessions.length})
              </button>
            )}
            <button onClick={()=>{fetchSRT();fetchQS();}}
              style={{ background:C_SHARED.fog,color:C_SHARED.muted,border:`1.5px solid ${C_SHARED.border}`,
                borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
              {isLoading?"⏳":"↺ Refresh"}
            </button>
          </div>
        </div>

        {srtSessions.length>=1&&<SRTAnalytics sessions={srtSessions} KOSHAS={KOSHAS} childName=""/>}

        <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:14 }}>
          <div style={{ flex:1,height:1,background:C_SHARED.border }}/>
          <span style={{ fontSize:11,fontWeight:700,color:C_SHARED.muted }}>ASSESSMENT RECORDS</span>
          <div style={{ flex:1,height:1,background:C_SHARED.border }}/>
        </div>

        <div style={{ display:"flex",gap:10,marginBottom:14,flexWrap:"wrap" }}>
          {[{icon:"🎮",label:"Full SRT (90 items)",bg:"#EBF2FA",color:C_SHARED.blue},
            {icon:"⚡",label:"Quick Screener (10 activities)",bg:"#FEF0E6",color:"#C2620A"}].map(l=>(
            <div key={l.label} style={{ display:"flex",alignItems:"center",gap:6,background:l.bg,
              padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:700,color:l.color }}>
              {l.icon} {l.label}
            </div>
          ))}
        </div>

        {isLoading&&merged.length===0&&(
          <div style={{ textAlign:"center",padding:40,color:C_SHARED.muted }}>
            <div style={{ fontSize:28,marginBottom:10 }}>⏳</div><div style={{ fontSize:13 }}>Loading history…</div>
          </div>
        )}
        {!isLoading&&merged.length===0&&(
          <div style={{ background:"white",borderRadius:14,padding:40,textAlign:"center",color:C_SHARED.muted }}>
            <div style={{ fontSize:36,marginBottom:12 }}>📋</div>
            <div style={{ fontWeight:700,fontSize:15,marginBottom:6 }}>No history yet</div>
            <div style={{ fontSize:12 }}>Saved SRT sessions and quick screenings will appear here.</div>
          </div>
        )}

        <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
          {merged.map((item,i)=>
            item._type==="srt"?(
              <SRTCard key={`srt-${item._id}`} sess={item} index={i} isFirst={i===0}
                isCurrent={item._id===currentSessId} isLoading={loadingItem}
                onLoad={handleLoadSRT} onDownload={handleDownloadSingle}
                onPrint={printSRT?()=>printSRT(item):null}
                KOSHAS={KOSHAS} levelFor={levelFor}/>
            ):(
              <QSCard key={`qs-${item._id}`} result={item} isFirst={i===0}
                isLoading={loadingItem} onLoad={onLoadQS?handleLoadQS:null}
                onPrint={printScreening?()=>printScreening(item):null}
                DOMAINS={DOMAINS}/>
            )
          )}
        </div>
      </div>
    );
  }

  /* ── SRT-ONLY MODE ── */
  return (
    <div>
      {srtSessions.length>=1&&<SRTAnalytics sessions={srtSessions} KOSHAS={KOSHAS} childName=""/>}

      {srtSessions.length>=1&&(
        <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:18 }}>
          <div style={{ flex:1,height:1,background:C_SHARED.border }}/>
          <span style={{ fontSize:11,fontWeight:700,color:C_SHARED.muted }}>SESSION RECORDS</span>
          <div style={{ flex:1,height:1,background:C_SHARED.border }}/>
        </div>
      )}

      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14 }}>
        <div>
          <div style={{ fontWeight:900,fontSize:16 }}>Full SRT Sessions</div>
          <div style={{ fontSize:12,color:C_SHARED.muted,marginTop:2 }}>
            {loadingSRT?"Loading…":`${srtSessions.length} session${srtSessions.length!==1?"s":""} saved`}
          </div>
        </div>
        <div style={{ display:"flex",gap:8,alignItems:"center" }}>
          {srtSessions.length>1&&(
            <button onClick={handleDownloadAll}
              style={{ background:"#0B1F35",color:"white",border:"none",borderRadius:8,
                padding:"7px 16px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                display:"flex",alignItems:"center",gap:5 }}>
              ⬇ Download All ({srtSessions.length} sessions)
            </button>
          )}
          <button onClick={fetchSRT}
            style={{ background:C_SHARED.fog,color:C_SHARED.muted,border:`1.5px solid ${C_SHARED.border}`,
              borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
            {loadingSRT?"⏳":"↺"}
          </button>
        </div>
      </div>

      {loadingSRT&&srtSessions.length===0&&(
        <div style={{ textAlign:"center",padding:30,color:C_SHARED.muted,fontSize:13 }}>⏳ Loading sessions…</div>
      )}
      {!loadingSRT&&srtSessions.length===0&&(
        <div style={{ background:"white",borderRadius:12,padding:"24px 20px",textAlign:"center",
          color:C_SHARED.muted,marginBottom:24,border:`1.5px solid ${C_SHARED.border}` }}>
          <div style={{ fontSize:28,marginBottom:8 }}>📋</div>
          <div style={{ fontWeight:700,fontSize:13 }}>No SRT sessions yet</div>
        </div>
      )}

      <div style={{ display:"flex",flexDirection:"column",gap:10,marginBottom:30 }}>
        {srtSessions.map((sess,i)=>(
          <SRTCard key={sess._id} sess={sess} index={i} isFirst={i===0}
            isCurrent={sess._id===currentSessId} isLoading={loadingItem}
            onLoad={handleLoadSRT} onDownload={handleDownloadSingle}
            onPrint={printSRT?()=>printSRT(sess):null}
            KOSHAS={KOSHAS} levelFor={levelFor}/>
        ))}
      </div>

      {/* Quick Screener section */}
      <div style={{ borderTop:`2px dashed ${C_SHARED.border}`,paddingTop:24 }}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14 }}>
          <div>
            <div style={{ display:"flex",alignItems:"center",gap:8 }}>
              <span style={{ fontWeight:900,fontSize:16 }}>Quick Screener Results</span>
              <span style={{ background:"#FEF0E6",color:"#C2620A",fontSize:9,fontWeight:800,padding:"2px 8px",borderRadius:8 }}>⚡</span>
            </div>
            <div style={{ fontSize:12,color:C_SHARED.muted,marginTop:2 }}>
              {loadingQS?"Loading…":`${qsResults.length} screening${qsResults.length!==1?"s":""} saved`}
            </div>
          </div>
          <button onClick={fetchQS}
            style={{ background:C_SHARED.fog,color:C_SHARED.muted,border:`1.5px solid ${C_SHARED.border}`,
              borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
            {loadingQS?"⏳":"↺"}
          </button>
        </div>

        {loadingQS&&qsResults.length===0&&(
          <div style={{ textAlign:"center",padding:30,color:C_SHARED.muted,fontSize:13 }}>⏳ Loading screenings…</div>
        )}
        {!loadingQS&&qsResults.length===0&&(
          <div style={{ background:"white",borderRadius:12,padding:"24px 20px",textAlign:"center",
            color:C_SHARED.muted,border:`1.5px solid ${C_SHARED.border}` }}>
            <div style={{ fontSize:28,marginBottom:8 }}>⚡</div>
            <div style={{ fontWeight:700,fontSize:13 }}>No quick screenings yet</div>
            <div style={{ fontSize:12,marginTop:4 }}>Run the Quick Screener for this child to see results here.</div>
          </div>
        )}

        <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
          {qsResults.map((r,i)=>(
            <QSCard key={r._id} result={r} isFirst={i===0} isLoading={loadingItem}
              onLoad={onLoadQS?handleLoadQS:null}
              onPrint={printScreening?()=>printScreening(r):null}
              DOMAINS={DOMAINS}/>
          ))}
        </div>
      </div>
    </div>
  );
}